import React, { useState, useEffect } from 'react';
import { Input, Typography, message, Modal, Button } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { PointerSensor, useSensor } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const { Title } = Typography;

// Component chính để chọn và sắp xếp video từ YouTube
// Props: 
// - value: Mảng ban đầu gồm các đối tượng { id: string, url: string }
// - onChange: Hàm callback trả về danh sách video đã chọn và sắp xếp sau mỗi thay đổi
//   Tham số: (newVideos: { id: string, url: string }[]) - Mảng các đối tượng video bao gồm ID và URL
const YoutubeVideoSelector = ({ value, onChange }) => {
  const [url, setUrl] = useState(''); // State lưu URL video
  const [videos, setVideos] = useState(value ?? []); // State lưu danh sách video khởi tạo từ value
  const [isModalVisible, setIsModalVisible] = useState(false); // State để kiểm soát modal
  const sensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 10,
    },
  });
  // Cập nhật state nếu value thay đổi từ bên ngoài
  useEffect(() => {
    console.log("value change", value)
    if (value) {
      setVideos(value );
    } else {
      setVideos([]);
    }

  }, [value]);

  const handleUrlChange = (e) => setUrl(e.target.value); // Hàm xử lý khi thay đổi URL

  // Hàm trích xuất ID video từ URL YouTube
  const extractVideoId = (url) => {
    const match = url.match(/(?:https?:\/\/)?(?:www\.)?youtu(?:\.be\/|be\.com\/watch\?v=)([\w-]+)/);
    return match ? match[1] : null;
  };

  // Hàm xử lý khi người dùng thêm video
  const handleSubmit = () => {
    const videoId = extractVideoId(url);
    if (videoId) {
      if (!videos.some(video => video.id === videoId)) {
        const newVideo = { id: videoId, url: url };
        const newVideos = [...videos, newVideo];
        setVideos(newVideos);
        onChange(newVideos);
        message.success('Đã thêm video thành công!');
      } else {
        message.warning('Video này đã được chọn rồi!');
      }
      setUrl('');
      setIsModalVisible(false);
    } else {
      message.error('URL YouTube không hợp lệ!');
    }
  };

  // Hàm xoá video khỏi danh sách
  const handleRemove = (index) => {
    const newVideos = videos.filter((_, i) => i !== index);
    setVideos(newVideos);
    onChange(newVideos);
  };

  // Xử lý kéo thả sắp xếp video
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = videos.findIndex(video => video.id === active.id);
      const newIndex = videos.findIndex(video => video.id === over.id);
      const newVideos = arrayMove(videos, oldIndex, newIndex);
      setVideos(newVideos);
      onChange(newVideos);
    }
  };

  // Component con hiển thị từng video
  const VideoItem = ({ video }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: video.id });

    // Style của từng video
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '10px',
      padding: '8px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      background: '#fff',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      cursor: 'move',
      position: 'relative'
    };

    return (
      <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
        {/* Hiển thị thumbnail video */}
        <img src={`https://img.youtube.com/vi/${video.id}/hqdefault.jpg`} alt="Video thumbnail" style={{ width: '120px', height: '90px', borderRadius: '4px' }} />
        {/* Nút xoá video */}
        <Button onClick={() => handleRemove(videos.indexOf(video))} type='link' style={{ position: 'absolute', top: '5px', right: '5px' }}>
          <DeleteOutlined />
        </Button>

      </div>
    );
  };

  return (
    <>

      <DndContext sensors={[sensor]} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={['add-button', ...videos.map(video => video.id)]} strategy={horizontalListSortingStrategy}>
          <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', padding: '8px' }}>
            {/* Nút thêm video */}
            <div
              onClick={() => setIsModalVisible(true)}
              style={{
                width: '120px',
                height: '90px',
                border: '1px solid #ddd',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                borderRadius: '4px',
                background: '#fff',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              <PlusOutlined style={{ fontSize: '32px', color: '#999' }} />
            </div>
            {/* Hiển thị danh sách video */}
            {videos.map((video) => (
              <VideoItem key={video.id} video={video} />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* Modal thêm video mới */}
      <Modal
        title="Thêm Video YouTube"
        open={isModalVisible}
        onOk={handleSubmit}
        onCancel={() => setIsModalVisible(false)}
        okText="Thêm"
        cancelText="Hủy"
      >
        <Input value={url} onChange={handleUrlChange} placeholder="Dán link YouTube vào đây..." />
      </Modal>
    </>
  );
};

export default YoutubeVideoSelector;
